// Return Status stages (2026-10-21, docs/decisions/2026-10-21-return-status-stages.md).
//
// A return is recorded in TRQ only when the warehouse processes it (the RTN),
// which can be days after it arrives. Until then the stage comes from the
// return label on the carrier's site, which CXHub cannot read yet, so the
// agent chooses it, with the date the carrier shows:
//   in_transit  "Return in transit to us", the carrier's estimated delivery
//               date: the template's INSERTDATE.
//   delivered   "Return delivered to our FC", the delivered date: the first
//               INSERTDATE; a later INSERTDATE (Website "Refund Delivered":
//               "expected to appear back by") is that date plus
//               RETURN_REFUND_EXPECTED_BUSINESS_DAYS business days.
// Shared so the server (the stage's draft) and the UI (the agent's date) fill
// the same way.
import { addBusinessDays } from './business-days.js'
import { isIsoDate } from './tasks.js'

export const RETURN_STAGE_IN_TRANSIT = 'in_transit'
export const RETURN_STAGE_DELIVERED = 'delivered'
// Delivered date to the refund's "expected back by" date (prod example:
// delivered 10/20, expected 10/27).
export const RETURN_REFUND_EXPECTED_BUSINESS_DAYS = 5

export const RETURN_STAGE_OPTIONS = Object.freeze([
  Object.freeze({ value: RETURN_STAGE_IN_TRANSIT, label: 'Return in transit to us', dateRequired: true, dateLabel: "Carrier's estimated delivery date" }),
  Object.freeze({ value: RETURN_STAGE_DELIVERED, label: 'Return delivered to our FC', dateRequired: true, dateLabel: 'Delivered date' }),
])

function mdy(iso) {
  const [y, m, d] = iso.split('-')
  return `${m}/${d}/${y}`
}

// The expected-back-by date for a delivered date (YYYY-MM-DD), or null.
// businessDays (2026-10-29): the stage step's refund window
// (params.refundOverdue.windowBusinessDays), so the "by" date and the
// overdue refund step use the same number.
export function returnRefundExpectedBy(deliveredDate, businessDays = RETURN_REFUND_EXPECTED_BUSINESS_DAYS) {
  const n = Number.isInteger(Number(businessDays)) && businessDays !== null ? Number(businessDays) : RETURN_REFUND_EXPECTED_BUSINESS_DAYS
  return isIsoDate(deliveredDate) ? addBusinessDays(deliveredDate, n) : null
}

// The stage's template text with its dates. date: YYYY-MM-DD. Without a valid
// date the text is returned unchanged, so INSERTDATE stays and Send stays
// blocked by the placeholder rule.
// The overdue refund's reply (Refund Confirmation, 2026-10-29) is filled when
// it is built: its dates are left as they are.
export function fillReturnStageDates(text, { stage, date, expectedBusinessDays = RETURN_REFUND_EXPECTED_BUSINESS_DAYS } = {}) {
  const s = String(text ?? '')
  if (!isIsoDate(date) || !s.includes('INSERTDATE')) return s
  const first = s.indexOf('INSERTDATE')
  let out = `${s.slice(0, first)}${mdy(date)}${s.slice(first + 'INSERTDATE'.length)}`
  if (stage === RETURN_STAGE_DELIVERED && out.includes('INSERTDATE')) {
    out = out.replaceAll('INSERTDATE', mdy(returnRefundExpectedBy(date, expectedBusinessDays)))
  }
  return out
}
